import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { ErrorList, InputTableColumns } from '../../../../components/InputTable/InputTable.type';
import { AxiosResponse } from 'axios';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  IControlTaskDistributionPatch,
  IControlTasksDistribution,
} from './ControlTasksDistribution.types';
import { useLayoutContext } from '../../../../Context/Layout.context';
import { InputTable } from '../../../../components/InputTable/InputTable.component';
import { OverflowTypograpy } from '../../../../components/OverflowTypography/OverflowTypography.component';
import { InputAdornment, Typography } from '@mui/material';
import { AssessmentSessionState } from '../../../Dashboard/Dashboard.types';
import { InactiveProcessBadge } from '../InactiveProcessBadge/InactiveProcessBadge.component';
import { OpenAssessmentSessionCell } from './Subcomponents/OpenAssessmentSessionCell.component';
import { InputTextField } from '../../../../components/InputTable/InputTextField/InputTextField.component';
import { InputSelection } from '../../../../components/InputTable/InputSelection/InputSelection.component';
import { getChangesFromArrayOfObject } from '../../../../utils/getChangesFromArrayOfObject';
import { saveControlTasksDistribution } from '../../../../services/exams/examSheet/examSheet.service';
import { AssessmentSessionProcessTypeId } from '../../../../services/exams/assessmentSession/assessments.types';
import { blockIconForInput } from '../../../../components/EditSession/BlockItemForInput';

interface ControlTasksDistributionProps {
  apiResponse: string;
  items: IControlTasksDistribution[];
  onClose: () => void;
  reload: () => void;
  isLoading: boolean;
  isExaminerView: boolean;
  onChange?: (isDataChanged: boolean) => void;
}

const getControlTasksCount = (row: IControlTasksDistribution): number | null => {
  return row.totalLimit && row.tasksPercentage
    ? row.totalLimit - Math.ceil((row.totalLimit * 100) / (100 + row.tasksPercentage))
    : null;
};

export const ControlTasksDistribution: React.FC<ControlTasksDistributionProps> = ({
  reload,
  isExaminerView,
  onClose,
  apiResponse,
  onChange,
  isLoading,
  items,
}) => {
  const [invalidType, setInvalidType] = useState<ErrorList[]>([]);
  const { openAlert } = useLayoutContext();
  const [parameters, setParamaters] = useState<IControlTasksDistribution[]>(items);
  const [isEdited, setIsEdited] = useState<boolean>(false);
  const { t } = useTranslation();

 const controlTasksDistributionColumns: InputTableColumns[] = [
   {
     id: 'examTaskGroupName',
     label: t('sessionsParameters:controlTasksDistribution.table.examTaskGroupName'),
   },
   {
     id: 'assessmentSessionStateId',
     label: '',
   },
   {
     id: 'totalLimit',
     label: t('sessionsParameters:controlTasksDistribution.table.totalLimit'),
   },
   {
     id: 'isFrequencyConstant',
     label: t('sessionsParameters:controlTasksDistribution.table.isFrequencyConstant'),
   },
   {
     id: 'tasksPercentage',
     label: t('sessionsParameters:controlTasksDistribution.table.tasksPercentage'),
   },
   {
     id: 'convertedNumber',
     label: t('sessionsParameters:controlTasksDistribution.table.convertedNumber'),
   },
 ];

  const handleValueCallback = useCallback(
    (key: keyof IControlTasksDistribution, value: number | null | boolean, id: string | number) => {
      setParamaters((state: IControlTasksDistribution[]) => {
        return state.map((s: IControlTasksDistribution) => {
          if (s.examTaskGroupId === id) {
            if (key == 'isFrequencyConstant') {
              s[key] = !Boolean(value);
            } else {
              s[key] = value as never;
            }
          }
          return s;
        });
      });
    },
    [parameters, setParamaters],
  );

  const validate = (row: IControlTasksDistribution, rowKey: keyof IControlTasksDistribution): ErrorList[] => {
    const errorList: ErrorList[] = [];
    const { tasksPercentage, isFrequencyConstant } = row;
    const isValidPercentage: boolean =
      tasksPercentage !== undefined && !isNaN(tasksPercentage) ? tasksPercentage > 0 && tasksPercentage < 101 : true;
    const isValidSelection = isFrequencyConstant !== null && Boolean(tasksPercentage);

    const controlTasksCount = getControlTasksCount(row);
    const isValidControlTasksCount: boolean =
      !controlTasksCount ||
      ((row.sampleAssessmentGroupMemberRequiredSolutionsCount &&
        controlTasksCount <= row.sampleAssessmentGroupMemberRequiredSolutionsCount) as boolean);

    if ((rowKey === 'tasksPercentage' || rowKey === 'isFrequencyConstant') && !isValidPercentage) {
      errorList.push({
        id: row.examTaskGroupId,
        type: 'tasksPercentage',
      });
    }

    if (rowKey === 'tasksPercentage' && !isValidSelection) {
      errorList.push({
        id: row.examTaskGroupId,
        type: rowKey,
      });
    }

    if (isValidPercentage && !isValidControlTasksCount) {
      errorList.push({
        id: row.examTaskGroupId,
        type: 'sampleAssessmentGroupMemberRequiredSolutionsCount',
      });
    }

    return errorList;
  };

  const checkRequired = (isInvalidRow: ErrorList | undefined): boolean => {
    if (isInvalidRow !== undefined) {
      if (isInvalidRow.type === 'isFrequencyConstant') {
        return true;
      } else return false;
    } else return false;
  };

  const valueValidation = (row: IControlTasksDistribution, rowKey: keyof IControlTasksDistribution): boolean => {
    const isValid = validate(row, rowKey);
    if (!isValid.length) {
      setInvalidType((prev) => prev.filter((e) => e.id !== row.examTaskGroupId));
      return isValid.length > 0;
    } else {
      setInvalidType((prev) => [...prev, ...isValid]);
      return isValid.length > 0;
    }
  };

  const getValidationMessage = (error: ErrorList): string => {
    switch (error.type) {
      case 'sampleAssessmentGroupMemberRequiredSolutionsCount':
        return 'Liczba obliczonych przez system kontrolny RGZ nie może przekraczać liczby ocen wzorcowych';
      default:
        return 'Podana wartość jest nieprawidłowa. Prawidłowa wartość powinna mieścić się w zakresie liczb od 1 do 100%';
    }
  };

  useEffect(() => {
    setIsEdited(JSON.stringify(parameters) !== apiResponse);
  }, [parameters, apiResponse]);

  const getRowComponent = (
    type: string,
    value: IControlTasksDistribution,
    isValid: boolean,
  ): React.ReactNode | string | null => {
    if (value.isProcessActive) {
      const disabled = value.assessmentSessionStateId !== AssessmentSessionState.New;
      switch (type) {
        case 'examTaskGroupName':
          return (
            <OverflowTypograpy
              style={{
                display: 'inline-block',
                fontWeight: 'bolder',
                fontSize: '1rem',
                color: '#3C419A',
                width: '130px',
              }}
            >
              {value[type]}
            </OverflowTypograpy>
          );
        case 'totalLimit':
          return <Typography sx={{ fontSize: 14, fontWeight: 400, marginLeft: 1.5 }}>{value[type]}</Typography>;
        case 'tasksPercentage':
          const isRequired = invalidType.some(
            (x) => x.id === value.examTaskGroupId && x.type === 'isFrequencyConstant',
          );
          return (
            <div style={{ margin: 8}}>
              <InputTextField<IControlTasksDistribution>
                focused={isRequired}
                idRow={value.examTaskGroupId}
                disabled={disabled || value.isFrequencyConstant === null}
                handleValueChange={handleValueCallback}
                value={value}
                isValid={isValid}
                rowKey={type}
                validation={valueValidation}
                inputLabel={'Procentowa liczba zadań'}
                ariaLabel={`Procentowa liczba zadań kontrolnych dla grupy zadań ${value.examTaskGroupName}`}
                InputProps={{
                  startAdornment: blockIconForInput(disabled || value.isFrequencyConstant === null, {marginRight:'0px'}),
                  endAdornment: Boolean(value.tasksPercentage) ? (
                    <InputAdornment position="end">%</InputAdornment>
                  ) : undefined,
                  style: {
                    width: '220px',
                  },
                }}
                required
              />
            </div>
          );
        case 'isFrequencyConstant':
          const val = value[type] !== null && value[type] !== undefined ? (value[type] ? 0 : 1) : '';
          return (
            <InputSelection<IControlTasksDistribution>
              sx={{ width: 230 }}
              rowKey={type}
              idRow={value.examTaskGroupId}
              disabled={disabled}
              items={['Częstotliwość stała', 'Częstotliwość liniowo malejąca']}
              inputLabel='Częstotliwość przydzielania zadań'
              ariaLabel={`Częstotliwość przydzielania zadań kontrolnych w procesie oceniania dla grupy zadań ${value.examTaskGroupName}`}
              inputProps={{
                'aria-label': `Wybierz rodzaj częstotliwośći`,
              }}
              handleValueChange={handleValueCallback}
              validation={valueValidation}
              row={value}
              value={val}
            />
          );
        case 'convertedNumber':
          const controlTasksCount = getControlTasksCount(value);
          const color: string =
            value.tasksPercentage &&
            value.tasksPercentage > 0 &&
            value.tasksPercentage < 101 &&
            (!value.sampleAssessmentGroupMemberRequiredSolutionsCount ||
              !controlTasksCount ||
              controlTasksCount <= value.sampleAssessmentGroupMemberRequiredSolutionsCount)
              ? '#3560FF'
              : '#BC0000';

          return value.totalLimit && value.tasksPercentage ? (
            <Typography sx={{ marginLeft: 1.5, fontSize: 14, fontWeight: 700, color: color }}>
              {getControlTasksCount(value)}
            </Typography>
          ) : null;

        case 'assessmentSessionStateId':
          return disabled ? <OpenAssessmentSessionCell /> : null;
        default:
          return null;
      }
    } else {
      switch (type) {
        case 'examTaskGroupName':
          return (
            <OverflowTypograpy
              style={{
                display: 'inline-block',
                fontWeight: 'bolder',
                fontSize: '1rem',
                color: '#3C419A',
                width: '130px',
              }}
            >
              {value[type]}
            </OverflowTypograpy>
          );
        case 'assessmentSessionStateId':
          return <InactiveProcessBadge processType={AssessmentSessionProcessTypeId.Control} />;
        default:
          return null;
      }
    }
  };

  const handleDiscardCallback = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    onChange && onChange(isEdited);
  }, [isEdited]);

  const handleSaveCallback = useCallback(() => {
    const apiData: IControlTasksDistribution[] = JSON.parse(apiResponse);
    const filterData: IControlTasksDistribution[] = getChangesFromArrayOfObject<IControlTasksDistribution>(
      apiData,
      parameters,
    );
    const data: IControlTaskDistributionPatch[] = filterData.map((x) => {
      return {
        examTaskGroupId: x.examTaskGroupId,
        isFrequencyConstant: Boolean(x.isFrequencyConstant),
        tasksPercentage: x.tasksPercentage ?? 0,
      };
    });

    saveControlTasksDistribution(data, isExaminerView)
      .then((res: AxiosResponse<boolean>) => {
        if (res.data) {
          openAlert('success');
          setIsEdited(false);
          reload();
        } else {
          openAlert('error');
        }
      })
      .catch(() => {
        openAlert('error');
      });
  }, [parameters, onClose]);

  useEffect(() => {
    items.forEach((item: IControlTasksDistribution) => {
      valueValidation(item, 'sampleAssessmentGroupMemberRequiredSolutionsCount');
    });
  }, []);

  return (
    <InputTable<IControlTasksDistribution>
      columns={controlTasksDistributionColumns}
      emptyMessage={t('toleranceRange:message.empty')}
      parameters={parameters}
      items={items}
      keyParam={'examTaskGroupId'}
      invalidType={invalidType}
      headerText={t('sessionsParameters:controlTasksDistribution.header')}
      onCancel={handleDiscardCallback}
      onSave={handleSaveCallback}
      getValidationMessage={getValidationMessage}
      getRowComponent={getRowComponent}
      edited={isEdited}
      isLoading={isLoading}
      activeProcessRow={'isProcessActive'}
      checkRequired={checkRequired}
    />
  );
};
